import apiClient from './_axios';

/** Team access (Ray 2026-10-04) — see server controllers/team.ts. */

/** 'full' = Full access; 'custom' = only the ticked pages + switches. */
export type TeamAccess = 'full' | 'custom';

/** The operator's Piso WiFi side-menu pages (ids/labels = SideNav). Mirrors
 *  server utils/teamAccess.ts TEAM_PAGES (Ray 2026-10-05) — change both. */
export const TEAM_PAGES = [
   { key: 'wifi', label: 'Dashboard' },
   { key: 'wifi-devices', label: 'Devices' },
   { key: 'wifi-sessions', label: 'Sessions' },
   { key: 'wifi-vouchers', label: 'Vouchers' },
   { key: 'wifi-data-plans', label: 'Data Plans' },
   { key: 'wifi-networks', label: 'Networks' },
   { key: 'wifi-team', label: 'Team' },
] as const;
export type TeamPage = (typeof TEAM_PAGES)[number]['key'];

export const TEAM_SWITCHES = [
   { key: 'transferCredits', label: 'Allow transfer credits' },
   { key: 'deleteDevice', label: 'Allow delete device' },
   { key: 'showMoney', label: 'Show money amounts' },
] as const;
export type TeamSwitch = (typeof TEAM_SWITCHES)[number]['key'];
export type TeamSwitches = Record<TeamSwitch, boolean>;
export const NO_SWITCHES: TeamSwitches = { transferCredits: false, deleteDevice: false, showMoney: false };

/** "Full access", "Devices, Sessions · see money amounts", "No pages yet". */
export function accessSummary(access: TeamAccess, pages: TeamPage[], switches: TeamSwitches = NO_SWITCHES): string {
   if (access === 'full') return 'Full access';
   const p = TEAM_PAGES.filter((x) => pages.includes(x.key)).map((x) => x.label);
   const sw = TEAM_SWITCHES.filter((x) => switches[x.key]).map((x) => x.label.replace(/^Allow /, '').replace(/^Show /, 'see '));
   const parts = [p.join(', '), sw.join(', ')].filter(Boolean);
   return parts.length ? parts.join(' · ') : 'No pages yet';
}

export interface TeamMemberRow {
   _id: string;
   email: string;
   memberName: string | null;
   access: TeamAccess;
   pages: TeamPage[];
   switches: TeamSwitches;
   accessSummary: string;
   /** 'seller-auto' = made when this person sold you a vendo (Ray 2026-10-05). */
   source?: 'invite' | 'seller-auto';
   status: 'pending' | 'active';
   invitedAt: string;
   acceptedAt: string | null;
   inviteExpiresAt: string | null;
}

export interface TeamActivityRow {
   _id: string;
   actorName: string;
   actorEmail: string;
   method: string;
   path: string;
   status: number;
   group?: string | null;
   at: string;
}

export interface Workspace {
   ownerId: string;
   ownerName: string;
   ownerEmail: string;
   access: TeamAccess;
   pages: TeamPage[];
   switches: TeamSwitches;
   accessSummary?: string;
   /** When this account was shared with me (accepted), for the date shortcuts. */
   sharedAt?: string;
   /** Fleet row (admins/staff): opened as SmartFi support, not via a share. */
   support?: boolean;
   /** The owner's vendos, for the Accounts search (name / device ID). */
   devices?: { name: string; deviceId: string }[];
}

export type GrantBody = { access: TeamAccess; pages: TeamPage[]; switches: TeamSwitches };

export async function listTeamApi(): Promise<{ members: TeamMemberRow[] }> {
   return (await apiClient.get('/team')).data;
}
export async function inviteTeamApi(body: GrantBody & { email: string }): Promise<{ member: TeamMemberRow; emailed: boolean }> {
   return (await apiClient.post('/team', body)).data;
}
export async function updateTeamApi(
   id: string,
   body: GrantBody
): Promise<{ member: TeamMemberRow }> {
   return (await apiClient.patch(`/team/${id}`, body)).data;
}
export async function removeTeamApi(id: string): Promise<{ ok: boolean }> {
   return (await apiClient.delete(`/team/${id}`)).data;
}
export async function listTeamActivityApi(params: {
   page: number;
   limit: number;
   q?: string;
}): Promise<{ rows: TeamActivityRow[]; total: number; page: number; limit: number }> {
   return (await apiClient.get('/team/activity', { params })).data;
}
export async function listWorkspacesApi(params?: { scope?: 'fleet'; q?: string; limit?: number; from?: string; to?: string }): Promise<{ workspaces: Workspace[]; scope?: string }> {
   return (await apiClient.get('/team/workspaces', { params })).data;
}
export async function acceptTeamInviteApi(token: string): Promise<{ workspace: Workspace }> {
   return (await apiClient.post('/team/accept', { token })).data;
}

/** An invite waiting for ME (by my email) — shown as a banner on my own dashboard. */
export interface PendingInvite {
   id: string;
   ownerId: string;
   ownerName: string;
   ownerEmail: string;
   accessSummary: string;
   invitedAt: string;
}
export async function listMyInvitesApi(): Promise<{ invites: PendingInvite[] }> {
   return (await apiClient.get('/team/invites')).data;
}
/** Accept straight from the dashboard banner — no email link needed. */
export async function acceptTeamInviteByIdApi(id: string): Promise<{ workspace: Workspace }> {
   return (await apiClient.post('/team/accept', { id })).data;
}
export async function declineTeamInviteApi(id: string): Promise<{ ok: boolean }> {
   return (await apiClient.post(`/team/invites/${id}/decline`)).data;
}
